import { type StateCreator, type StoreApi, create } from 'zustand'
import { type PersistOptions, createJSONStorage, persist } from 'zustand/middleware'
import { logError } from '../utils/sentry'

type SetState<T> = (partial: T | Partial<T> | ((state: T) => T | Partial<T>), replace?: boolean) => void

type GetState<T> = () => T

export interface BaseStateWithLoadingAndError {
  loading?: boolean
  error?: string | string[] | null
  isInitialized?: boolean
  [key: string]: unknown
}

export type StoreActions = {
  reset: () => void
  setError: (error: string | string[] | null, context?: Record<string, unknown>) => void
  clearError: () => void
  setLoading: (loading: boolean) => void
}

export const createPersistedStore = <State extends BaseStateWithLoadingAndError, Actions>(
  name: string,
  initialState: State,
  actionsCreator: ActionsCreator<State, Actions>,
) => {
  try {
    type StoreState = State & Actions & StoreActions

    type PersistConfig = PersistOptions<StoreState, Partial<StoreState>>

    const persistConfig: PersistConfig = {
      name,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => {
        const { tempData, loading, error, isInitialized, ...persistedState } = state
        return persistedState as Partial<StoreState>
      },
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isInitialized = false
        }
      },
    }

    return create<StoreState>()(
      persist(
        (set, get) => ({
          ...initialState,
          ...actionsCreator(set as SetState<StoreState>, get),
          reset: () => {
            set(initialState as unknown as Partial<StoreState>)
          },
          setError: (error, context = {}) => {
            logError(`Store error in ${name}:`, { error, context })
            set({ error } as Partial<StoreState>)
          },
          clearError: () => {
            set({ error: null } as Partial<StoreState>)
          },
          setLoading: (loading) => {
            set({ loading } as Partial<StoreState>)
          },
        }),
        persistConfig,
      ),
    )
  } catch (error: unknown) {
    logError(`Error creating store ${name}:`, { error })
    const errorMessage = error instanceof Error ? error.message : String(error)
    throw new Error(`Failed to create store ${name}: ${errorMessage}`)
  }
}

type ActionsCreator<State extends BaseStateWithLoadingAndError, Actions> = (
  set: SetState<State & Actions & StoreActions>,
  get: GetState<State & Actions & StoreActions>,
) => Actions

export const createAsyncAction = <State extends BaseStateWithLoadingAndError, Result>(
  store: { setState: (state: Partial<State>) => void },
  asyncFn: (...args: unknown[]) => Promise<Result>,
): ((...args: unknown[]) => Promise<Result>) => {
  return async (...args: unknown[]): Promise<Result> => {
    store.setState({ loading: true, error: null } as unknown as Partial<State>)

    try {
      const result = await asyncFn(...args)
      store.setState({ loading: false } as unknown as Partial<State>)
      return result
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      logError('Async action failed:', { error })
      store.setState({ loading: false, error: errorMessage } as unknown as Partial<State>)
      throw new Error(`Async action failed: ${errorMessage}`)
    }
  }
}

export const debugMiddleware =
  <T>(config: { name: string; store: StateCreator<T> }) =>
  (set: SetState<T>, get: GetState<T>, api: StoreApi<T>): ReturnType<typeof config.store> => {
    return config.store(
      (...args) => {
        set(...args)
      },
      get,
      api,
    )
  }
